import dynamicAPI from '../dynamicAPI';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useMessage } from '../../context/AdminMessageContext';

const url = '/api/auth/curations';
const queryKey = 'curations';

export default function useCurationsHook(props) {
    const { page = 1, id, q = '', limit = 25, sort = '', order = 'desc' } = props || {};
    const queryClient = useQueryClient();
    const { setSuccessMessage, setErrorMessage } = useMessage();

    const getCurations = useQuery(
        [queryKey, page, limit, q, sort, order],
        async () => {
            const response = await dynamicAPI('get', `${url}?page=${page}&q=${q}&limit=${limit}&sort=${sort}&order=${order}`);
            if (response?.error) { setErrorMessage(response.error); } else { return response; }
        },
        { retry: 0, staleTime: 1000 * 15, enabled: props?.listEnabled !== false }
    );

    const getAllCurations = useQuery(
        ['curations-all', page, limit, q],
        async () => {
            const response = await dynamicAPI('get', `${url}/all?page=${page}&q=${q}&limit=${limit}`);
            if (response?.error) { setErrorMessage(response.error); } else { return response; }
        },
        { retry: 0, staleTime: 1000 * 15, enabled: false }
    );

    const getActiveCurations = useQuery(
        ['curations-active'],
        async () => {
            const response = await dynamicAPI('get', `${url}/active`);
            if (response?.error) { setErrorMessage(response.error); } else { return response; }
        },
        { retry: 0, staleTime: 1000 * 60, enabled: !!props?.activeEnabled }
    );

    const getCurationById = useQuery(
        [queryKey, id],
        async () => {
            const response = await dynamicAPI('get', `${url}/${id}`);
            if (response?.error) { setErrorMessage(response.error); } else { return response; }
        },
        { retry: 0, enabled: !!id }
    );

    const getCurationAccess = useQuery(
        ['curations-access'],
        async () => {
            const response = await dynamicAPI('get', `${url}/access`);
            if (response?.error) { setErrorMessage(response.error); } else { return response; }
        },
        { retry: 0, staleTime: 1000 * 30, enabled: false }
    );

    const postCuration = useMutation(
        async (obj) => await dynamicAPI('post', url, obj),
        {
            retry: 0,
            onSuccess: (data) => {
                if (data?.isSuccess) {
                    setSuccessMessage('Curation created successfully.');
                    queryClient.invalidateQueries([queryKey]);
                } else {
                    setErrorMessage(data?.error || 'Failed to create curation.');
                }
            },
            onError: (err) => setErrorMessage(err?.error || 'Error creating curation.'),
        }
    );

    const updateCuration = useMutation(
        async (obj) => await dynamicAPI('put', `${url}/${obj._id}`, obj),
        {
            retry: 0,
            onSuccess: (data) => {
                if (data?.isSuccess) {
                    setSuccessMessage('Curation updated successfully.');
                    queryClient.invalidateQueries([queryKey]);
                } else {
                    setErrorMessage(data?.error || 'Failed to update curation.');
                }
            },
            // A 409 is a concurrent-edit conflict; the editor handles it with a
            // tailored "reload latest?" dialog, so skip the generic toast.
            onError: (err) => {
                if (err?.status === 409) { return; }
                setErrorMessage(err?.error || 'Error updating curation.');
            },
        }
    );

    const deleteCuration = useMutation(
        async (deleteId) => await dynamicAPI('delete', `${url}/${deleteId}`, {}),
        {
            retry: 0,
            onSuccess: (data) => {
                if (data?.isSuccess) {
                    setSuccessMessage('Curation deleted successfully.');
                    queryClient.invalidateQueries([queryKey]);
                } else {
                    setErrorMessage(data?.error || 'Failed to delete curation.');
                }
            },
            onError: (err) => setErrorMessage(err?.error || 'Error deleting curation.'),
        }
    );

    const addCurationAccess = useMutation(
        async (userId) => await dynamicAPI('post', `${url}/access`, { userId }),
        {
            retry: 0,
            onSuccess: (data) => {
                if (data?.isSuccess) {
                    setSuccessMessage('Access granted.');
                    queryClient.invalidateQueries(['curations-access']);
                } else {
                    setErrorMessage(data?.error || 'Failed to grant access.');
                }
            },
            onError: (err) => setErrorMessage(err?.error || 'Error granting access.'),
        }
    );

    const removeCurationAccess = useMutation(
        async (userId) => await dynamicAPI('delete', `${url}/access/${userId}`, {}),
        {
            retry: 0,
            onSuccess: () => {
                setSuccessMessage('Access removed.');
                queryClient.invalidateQueries(['curations-access']);
            },
            onError: (err) => setErrorMessage(err?.error || 'Error removing access.'),
        }
    );

    return {
        getCurations,
        getAllCurations,
        getActiveCurations,
        getCurationById,
        getCurationAccess,
        postCuration,
        updateCuration,
        deleteCuration,
        addCurationAccess,
        removeCurationAccess,
    };
}
